// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/brushLibrary.js
//
// The user's brush library lives in this browser's IndexedDB only. Imported
// brushes are private: nothing here talks to the server, and collaborators
// only ever receive the small blob preview the canvas already shares.
//
//   brushes  schema-v2 records, keyed by id
//   assets   PNG blobs keyed by content hash; a texture shared by many
//            presets is stored once and removed when nothing references it
//   meta     recents, the active brush, migration markers
import { zipSync, strToU8 } from 'fflate';
import { BRUSH_SCHEMA_ID, BRUSH_SCHEMA_VERSION, migrateLegacyBrushRecord, normaliseBrushRecord } from './brushSchema.js';
import { canvasFromBlob, canvasToPngBlob, processCandidateAssets } from './tipProcessing.js';
import { buildRuntimePreset } from './runtimePreset.js';
import { renderBrushStrokePreview } from './brushPreview.js';
import { hashBytes } from './hash.js';
import { openZip } from './safeZip.js';

const DB_NAME = 'voxemera-brush-library';
const DB_VERSION = 1;
export const LIBRARY_SOFT_LIMIT_BYTES = 400 * 1024 * 1024;
export const NATIVE_EXPORT_TYPE = 'voxemera-brush-export';

let dbPromise = null;
const listeners = new Set();

// The canvas reloads its brush list when the library changes elsewhere
// (the importer, the library manager or another tab's restore).
export function subscribeLibrary(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function notifyLibraryChanged(detail = {}) {
  listeners.forEach((listener) => {
    try {
      listener(detail);
    } catch {}
  });
}

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is unavailable.'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('brushes')) db.createObjectStore('brushes', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('assets')) db.createObjectStore('assets', { keyPath: 'hash' });
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'key' });
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => {
      dbPromise = null;
      reject(request.error || new Error('Brush library could not be opened.'));
    };
  });
  return dbPromise;
}

function run(storeNames, mode, work) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(storeNames, mode);
    let result;
    Promise.resolve(work(tx)).then((value) => {
      result = value;
    }, reject);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error || new Error('Brush library transaction failed.'));
    tx.onabort = () => reject(tx.error || new Error('Brush library transaction aborted.'));
  }));
}

function requestValue(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function listRecords() {
  const records = await run(['brushes'], 'readonly', (tx) => requestValue(tx.objectStore('brushes').getAll()));
  return (records || []).map(normaliseBrushRecord).sort((a, b) => (a.library.order - b.library.order) || (a.createdAt - b.createdAt));
}

export function getMeta(key) {
  return run(['meta'], 'readonly', (tx) => requestValue(tx.objectStore('meta').get(key))).then((row) => (row ? row.value : undefined));
}

export function setMeta(key, value) {
  return run(['meta'], 'readwrite', (tx) => {
    tx.objectStore('meta').put({ key, value });
  });
}

export function getAssetBlob(hash) {
  if (!hash) return Promise.resolve(null);
  return run(['assets'], 'readonly', (tx) => requestValue(tx.objectStore('assets').get(hash))).then((row) => (row ? row.blob : null));
}

export async function putRecords(records, assets = []) {
  const clean = records.map(normaliseBrushRecord);
  await run(['brushes', 'assets'], 'readwrite', async (tx) => {
    const assetStore = tx.objectStore('assets');
    for (const asset of assets) {
      assetStore.put({ hash: asset.hash, blob: asset.blob, bytes: asset.blob.size, kind: asset.kind || 'tip', width: asset.width || 0, height: asset.height || 0 });
    }
    const brushStore = tx.objectStore('brushes');
    clean.forEach((record) => brushStore.put(record));
  });
  return clean;
}

export async function updateRecord(id, patch) {
  const records = await listRecords();
  const record = records.find((item) => item.id === id);
  if (!record) return null;
  const next = normaliseBrushRecord({
    ...record,
    ...patch,
    library: { ...record.library, ...(patch.library || {}) },
    settings: { ...record.settings, ...(patch.settings || {}) },
    updatedAt: Date.now(),
  });
  await putRecords([next]);
  return next;
}

export async function softDelete(ids, deleted = true) {
  const wanted = new Set(ids);
  const records = (await listRecords()).filter((record) => wanted.has(record.id));
  const now = Date.now();
  const next = records.map((record) => normaliseBrushRecord({ ...record, library: { ...record.library, deletedAt: deleted ? now : 0 } }));
  await putRecords(next);
  return next;
}

// Removes brushes for good and drops assets no remaining brush references.
export async function purge(ids) {
  const wanted = new Set(ids);
  const records = await listRecords();
  const keep = records.filter((record) => !wanted.has(record.id));
  const referenced = new Set();
  keep.forEach((record) => {
    record.assets.tips.forEach((tip) => referenced.add(tip.hash));
    if (record.assets.grain) referenced.add(record.assets.grain.hash);
    if (record.assets.preview) referenced.add(record.assets.preview.hash);
    if (record.assets.thumbnail) referenced.add(record.assets.thumbnail.hash);
  });
  await run(['brushes', 'assets'], 'readwrite', async (tx) => {
    const brushStore = tx.objectStore('brushes');
    ids.forEach((id) => brushStore.delete(id));
    const assetStore = tx.objectStore('assets');
    const keys = await requestValue(assetStore.getAllKeys());
    (keys || []).forEach((key) => {
      if (!referenced.has(key)) assetStore.delete(key);
    });
  });
}

export async function storageUsage() {
  const sizes = await run(['assets'], 'readonly', (tx) => requestValue(tx.objectStore('assets').getAll()));
  const library = (sizes || []).reduce((sum, row) => sum + (row.bytes || 0), 0);
  let quota = 0;
  let usage = 0;
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      quota = estimate.quota || 0;
      usage = estimate.usage || 0;
    }
  } catch {}
  return { library, quota, usage };
}

// --- Runtime loading -------------------------------------------------------

const canvasCache = new Map();
const urlCache = new Map();

async function canvasForHash(hash) {
  if (!hash) return null;
  if (canvasCache.has(hash)) return canvasCache.get(hash);
  const promise = getAssetBlob(hash).then(async (blob) => {
    if (!blob) return null;
    const canvas = await canvasFromBlob(blob, 4096);
    return canvas;
  }).catch(() => null);
  canvasCache.set(hash, promise);
  return promise;
}

async function urlForHash(hash) {
  if (!hash) return '';
  if (urlCache.has(hash)) return urlCache.get(hash);
  const blob = await getAssetBlob(hash);
  const url = blob ? URL.createObjectURL(blob) : '';
  urlCache.set(hash, url);
  return url;
}

// A light runtime preset (preview only, no tips) for library listings.
export async function loadRuntimeShell(record) {
  return buildRuntimePreset(record, {
    tips: [],
    previewUrl: await urlForHash(record.assets.preview && record.assets.preview.hash),
    thumbnailUrl: await urlForHash(record.assets.thumbnail && record.assets.thumbnail.hash),
  });
}

// Decodes every tip and the grain so the brush can paint.
export async function loadRuntimePreset(record) {
  const tips = await Promise.all(record.assets.tips.map((tip) => canvasForHash(tip.hash)));
  const grainCanvas = record.assets.grain ? await canvasForHash(record.assets.grain.hash) : null;
  if (grainCanvas && record.assets.grain) grainCanvas.grainSourceSize = record.assets.grain.width || grainCanvas.width;
  return buildRuntimePreset(record, {
    tips,
    grain: grainCanvas,
    previewUrl: await urlForHash(record.assets.preview && record.assets.preview.hash),
    thumbnailUrl: await urlForHash(record.assets.thumbnail && record.assets.thumbnail.hash),
  });
}

// --- Import ----------------------------------------------------------------

async function assetFromCanvas(canvas, kind) {
  const blob = await canvasToPngBlob(canvas);
  const hash = await hashBytes([kind, new Uint8Array(await blob.arrayBuffer())]);
  return { hash, blob, kind, width: canvas.grainSourceSize || canvas.width, height: canvas.height };
}

// Turns an inspected candidate into a record plus assets and a runtime
// preset. Pixels are decoded here, for selected brushes only.
function candidateRecord(candidate, processed, extra = {}) {
  const translation = candidate.translation || {};
  const settings = { ...(translation.settings || {}), tipColour: processed.tipColour };
  if (processed.spacing && candidate.app === 'gimp') settings.spacing = processed.spacing;
  return normaliseBrushRecord({
    id: extra.id || 'brush-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9),
    name: extra.name || candidate.name,
    library: { folder: extra.folder || candidate.folderPath, category: candidate.category },
    source: { ...candidate.source, contentHash: candidate.contentHash, importerVersion: 2 },
    assets: { tips: extra.tipRefs || processed.tips.map(() => ({ hash: 'pending' })), tipSelection: processed.tipSelection, grain: extra.grainRef || (processed.grain ? { hash: 'pending' } : null) },
    settings,
    dynamics: translation.dynamics,
    texture: translation.texture && processed.grain ? { ...translation.texture, hash: (extra.grainRef && extra.grainRef.hash) || 'pending' } : null,
    rendering: translation.rendering,
    engine: candidate.engine,
    compatibility: {
      ...translation.compatibility,
      notes: [...((translation.compatibility && translation.compatibility.notes) || []), ...(candidate.warnings || []), ...processed.warnings],
    },
  });
}

const preparedCache = new Map();

// Decodes a candidate's assets once; previews, the test canvas and the final
// import all reuse the result.
export function prepareCandidate(candidate) {
  if (preparedCache.has(candidate.key)) return preparedCache.get(candidate.key);
  const promise = processCandidateAssets(candidate).then((processed) => {
    const record = candidateRecord(candidate, processed, { id: 'preview-' + candidate.key });
    const runtime = buildRuntimePreset(record, { tips: processed.tips, grain: processed.grain });
    return { processed, record, runtime };
  });
  preparedCache.set(candidate.key, promise);
  promise.catch(() => preparedCache.delete(candidate.key));
  return promise;
}

export function forgetPreparedCandidates() {
  preparedCache.clear();
}

export async function previewCandidate(candidate, options = {}) {
  const prepared = await prepareCandidate(candidate);
  if (!prepared.runtime.paintable) return { ...prepared, canvas: null };
  return { ...prepared, canvas: renderBrushStrokePreview(prepared.runtime, { width: 320, height: 112, pixelRatio: 2, ...options }) };
}

export async function materialiseCandidate(candidate, { id, folder, name } = {}) {
  const { processed } = await prepareCandidate(candidate);
  if (!processed.tips.length && candidate.engine.id === 'stamp') throw new Error('No usable brush tip.');
  const assets = [];
  const tipRefs = [];
  for (const tip of processed.tips) {
    const asset = await assetFromCanvas(tip, 'tip');
    assets.push(asset);
    tipRefs.push({ hash: asset.hash, width: asset.width, height: asset.height });
  }
  let grainRef = null;
  if (processed.grain) {
    const asset = await assetFromCanvas(processed.grain, 'grain');
    assets.push(asset);
    grainRef = { hash: asset.hash, width: asset.width, height: asset.height };
  }
  const record = candidateRecord(candidate, processed, { id, folder, name, tipRefs, grainRef });
  const runtime = buildRuntimePreset(record, { tips: processed.tips, grain: processed.grain });
  if (runtime.paintable) {
    const preview = renderBrushStrokePreview(runtime, { width: 320, height: 112, pixelRatio: 2 });
    const asset = await assetFromCanvas(preview, 'preview');
    assets.push(asset);
    record.assets.preview = { hash: asset.hash };
    runtime.previewUrl = URL.createObjectURL(asset.blob);
    runtime.thumbnailDataUrl = runtime.previewUrl;
    urlCache.set(asset.hash, runtime.previewUrl);
  }
  runtime.brushRecord = record;
  return { record, assets, runtime };
}

// Duplicates are matched on content hash, never on name.
export async function findDuplicates(candidates) {
  const records = await listRecords();
  const byHash = new Map();
  records.forEach((record) => {
    if (record.source.contentHash && !record.library.deletedAt) byHash.set(record.source.contentHash, record);
  });
  const result = new Map();
  candidates.forEach((candidate) => {
    const match = byHash.get(candidate.contentHash);
    if (match) result.set(candidate.key, match);
  });
  return result;
}

// policy per candidate: 'skip' | 'replace' | 'keep'
export async function importCandidates(candidates, { duplicates = new Map(), policy = () => 'skip', signal, onProgress } = {}) {
  const imported = [];
  const failed = [];
  const records = await listRecords();
  let order = records.reduce((max, record) => Math.max(max, record.library.order), 0) + 1;
  for (let index = 0; index < candidates.length; index += 1) {
    if (signal && signal.aborted) break;
    const candidate = candidates[index];
    if (typeof onProgress === 'function') onProgress({ index, total: candidates.length, name: candidate.name });
    const duplicate = duplicates.get(candidate.key);
    const choice = duplicate ? policy(candidate, duplicate) : 'keep';
    if (duplicate && choice === 'skip') continue;
    try {
      const { record, assets, runtime } = await materialiseCandidate(candidate, {
        id: duplicate && choice === 'replace' ? duplicate.id : undefined,
        name: duplicate && choice === 'keep' ? `${candidate.name} (2)` : candidate.name,
      });
      record.library.order = duplicate && choice === 'replace' ? duplicate.library.order : order;
      order += 1;
      await putRecords([record], assets);
      runtime.brushRecord = record;
      imported.push(runtime);
    } catch (err) {
      failed.push({ name: candidate.name, message: err && err.message ? err.message : 'import failed' });
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  return { imported, failed };
}

// --- Legacy migration ------------------------------------------------------

async function blobFromDataUrl(dataUrl) {
  const response = await fetch(dataUrl);
  return response.blob();
}

// Copies brushes from the version-1 session library into this library. The
// old record is never modified or deleted, so a failure loses nothing.
export async function migrateLegacyLibrary(legacyBrushes) {
  if (await getMeta('migratedLegacyV1')) return [];
  const migrated = [];
  for (const legacy of Array.isArray(legacyBrushes) ? legacyBrushes : []) {
    try {
      const urls = [
        ...(Array.isArray(legacy.originalTipCanvasesDataUrls) && legacy.originalTipCanvasesDataUrls.length ? legacy.originalTipCanvasesDataUrls : [legacy.originalTipCanvasDataUrl || legacy.originalTipDataUrl]),
      ].filter((url) => typeof url === 'string' && url.startsWith('data:image/'));
      const assets = [];
      const tipHashes = [];
      for (const url of urls.slice(0, 128)) {
        const blob = await blobFromDataUrl(url);
        const hash = await hashBytes(['tip', new Uint8Array(await blob.arrayBuffer())]);
        assets.push({ hash, blob, kind: 'tip' });
        tipHashes.push(hash);
      }
      let grainHash = '';
      if (typeof legacy.originalGrainCanvasDataUrl === 'string' && legacy.originalGrainCanvasDataUrl.startsWith('data:image/')) {
        const blob = await blobFromDataUrl(legacy.originalGrainCanvasDataUrl);
        grainHash = await hashBytes(['grain', new Uint8Array(await blob.arrayBuffer())]);
        assets.push({ hash: grainHash, blob, kind: 'grain' });
      }
      const record = migrateLegacyBrushRecord(legacy, { tipHashes, grainHash });
      await putRecords([record], assets);
      migrated.push(record);
    } catch {}
  }
  await setMeta('migratedLegacyV1', { at: Date.now(), count: migrated.length });
  return migrated;
}

// --- Export and backup -----------------------------------------------------

async function assetsForRecords(records) {
  const hashes = new Set();
  records.forEach((record) => {
    record.assets.tips.forEach((tip) => hashes.add(tip.hash));
    if (record.assets.grain) hashes.add(record.assets.grain.hash);
    if (record.assets.preview) hashes.add(record.assets.preview.hash);
  });
  const files = {};
  for (const hash of hashes) {
    const blob = await getAssetBlob(hash);
    if (blob) files[`assets/${hash}.png`] = new Uint8Array(await blob.arrayBuffer());
  }
  return files;
}

// A .voxbrush file (ZIP): manifest.json + records + PNG assets.
export async function exportRecords(records, { kind = 'presets' } = {}) {
  const files = await assetsForRecords(records);
  files['manifest.json'] = strToU8(JSON.stringify({
    type: NATIVE_EXPORT_TYPE,
    kind,
    schema: BRUSH_SCHEMA_ID,
    schemaVersion: BRUSH_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    brushes: records.map((record) => ({ ...record, library: { ...record.library, deletedAt: 0 } })),
  }));
  return new Blob([zipSync(files, { level: 6 })], { type: 'application/zip' });
}

export function isNativeExport(bytes) {
  try {
    const zip = openZip(bytes);
    const manifest = zip.find('manifest.json');
    if (!manifest || manifest.size > 32 * 1024 * 1024) return false;
    return JSON.parse(zip.readText(manifest)).type === NATIVE_EXPORT_TYPE;
  } catch {
    return false;
  }
}

// Restores a .voxbrush file. Existing brushes with the same id are kept
// unless `replace` is set; restored copies get fresh ids instead.
export async function importNativeExport(bytes, { replace = false } = {}) {
  const zip = openZip(bytes);
  const manifestEntry = zip.find('manifest.json');
  if (!manifestEntry) throw new Error('Not a Voxemera brush export.');
  const manifest = JSON.parse(zip.readText(manifestEntry));
  if (manifest.type !== NATIVE_EXPORT_TYPE) throw new Error('Not a Voxemera brush export.');
  const existing = new Set((await listRecords()).map((record) => record.id));
  const assets = [];
  zip.entries.filter((entry) => /^assets\/[a-z0-9-]+\.png$/i.test(entry.path)).forEach((entry) => {
    const hash = entry.path.slice(7, -4);
    assets.push({ hash, blob: new Blob([zip.read(entry)], { type: 'image/png' }), kind: 'tip' });
  });
  const records = (Array.isArray(manifest.brushes) ? manifest.brushes : []).slice(0, 5000).map((raw) => {
    const record = normaliseBrushRecord(raw);
    if (existing.has(record.id) && !replace) record.id = 'brush-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
    return record;
  });
  await putRecords(records, assets);
  return records;
}

